import type { IconName } from '@forecastify/ui';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Which glyph a signed-in device gets — item 13.                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Every row drew `monitor`, so a phone, a tablet and a laptop were three identical icons — which
 * is worse than none: an icon that never varies teaches the eye to stop reading it, and this list
 * exists precisely so somebody can spot the device that is not theirs.
 *
 * ⚠️⚠️ **A USER-AGENT STRING IS A GUESS AND THIS TREATS IT AS ONE.** It is self-reported, freely
 * spoofed and full of lies for compatibility — every desktop Chrome still says `Mozilla` and
 * `Safari`. So the match is deliberately coarse and ordered from most specific to least, and an
 * unrecognised agent gets the neutral `monitor` rather than a wrong guess. Nothing here decides
 * anything but a picture.
 *
 * ⚠️ **`iPad` BEFORE `Mobile`, and iPadOS is why**: it reports `Macintosh` with a touch hint on
 * recent versions and `iPad` on older ones, and it also carries `Mobile` in some configurations. A
 * tablet matched as a phone is a small wrong; the order costs nothing.
 */
export function deviceIcon(userAgent: string | null): IconName {
  if (userAgent === null) return 'monitor';
  const agent = userAgent.toLowerCase();

  /* ⚠️ Tablets first — every one of them also matches a phone pattern. */
  if (agent.includes('ipad') || agent.includes('tablet')) return 'monitor';
  if (agent.includes('iphone') || agent.includes('android') || agent.includes('mobile')) {
    return 'smartphone';
  }
  return 'monitor';
}

/**
 * The device's name, shortened for a row.
 *
 * ⚠️⚠️ **THE FULL STRING STAYS AVAILABLE AS A TOOLTIP — item 14.** A user agent is two hundred
 * characters of version numbers, so a row shows the browser and the platform and the `title`
 * carries the rest. Truncating without keeping the original is how somebody loses the one detail
 * that would have told them which laptop this was.
 *
 * ⚠️ **A best-effort read, and `null` means *"say the whole thing"***, so the caller falls back to
 * the raw agent rather than to a made-up label.
 */
export function deviceName(userAgent: string | null): string | null {
  if (userAgent === null) return null;

  /* ⚠️ Order matters: Edge claims Chrome, Chrome claims Safari, and Safari claims neither. */
  const browser = /edg\//i.test(userAgent)
    ? 'Edge'
    : /opr\/|opera/i.test(userAgent)
      ? 'Opera'
      : /chrome\//i.test(userAgent)
        ? 'Chrome'
        : /firefox\//i.test(userAgent)
          ? 'Firefox'
          : /safari\//i.test(userAgent)
            ? 'Safari'
            : null;

  const platform = /windows/i.test(userAgent)
    ? 'Windows'
    : /iphone|ipad|ios/i.test(userAgent)
      ? 'iOS'
      : /mac os|macintosh/i.test(userAgent)
        ? 'macOS'
        : /android/i.test(userAgent)
          ? 'Android'
          : /linux/i.test(userAgent)
            ? 'Linux'
            : null;

  if (browser === null && platform === null) return null;
  if (browser === null) return platform;
  if (platform === null) return browser;
  return `${browser} · ${platform}`;
}
